import type { ModuleConfig } from '../types/module'

/**
 * Define a Kubdee module.
 *
 * Every module uses this function to declare its configuration,
 * schemas, routes, navigation, permissions, and hooks.
 *
 * @example
 * ```ts
 * import { defineModule } from '@kubdee/shared/module-system'
 *
 * export default defineModule({
 *   name: 'car-rental',
 *   label: 'ระบบเช่ารถ',
 *   icon: 'Car',
 *   version: '1.0.0',
 *   dependencies: ['core', 'accounting'],
 *   schemas: {},
 *   routes: {},
 *   navigation: { ... },
 *   permissions: {},
 *   hooks: {},
 * })
 * ```
 */
export function defineModule(config: ModuleConfig): ModuleConfig {
  // Validate required fields at definition time
  if (!config.name) {
    throw new Error('Module name is required')
  }
  if (!config.version) {
    throw new Error(`Module "${config.name}" must specify a version`)
  }
  if (!config.navigation) {
    throw new Error(`Module "${config.name}" must define navigation`)
  }

  // Default seedVersion to version if not provided
  const seedVersion = config.seedVersion ?? config.version

  return Object.freeze({ ...config, seedVersion })
}
